import { DateInputFormat, getDateFormat } from "@/utils/date-format";
import { z } from "zod";
import {
  Button,
  DateInput,
  FormSection,
  Grid,
  Option,
  SelectBox,
  TextAreaInput,
  TextInput,
} from "@monolith-forensics/monolith-ui";

import { FC } from "react";
import { useForm } from "@mantine/form";

import diffFormData from "@/components/Forms/utils/diffFormData";
import validateAsync from "@/components/Forms/utils/zod/validateAsync";
import resolveCustomFieldValidations from "@/components/Forms/utils/resolveCustomFieldValidations";

import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import SoftwareSelector from "@/components/Forms/components/Selectors/SoftwareSelector";
import CustomFieldRender from "@/components/Forms/components/CustomFieldRender";
import { useCustomFields, CustomField } from "./hooks/useCustomFields";
import { transformCustomAttributes } from "./utils/customAttributeUtils";

import FormatSelector from "@/components/Forms/components/Selectors/FormatSelector";
import AcquisitionTypeSelector from "@/components/Forms/components/Selectors/AcqTypeSelector";
import StorageSelector from "@/components/Forms/components/Selectors/StorageSelector";
import HashTypeSelector from "@/components/Forms/components/Selectors/HashTypeSelector";
import UserSelector from "@/components/Forms/components/Selectors/UserSelector";
import CustomAttribute from "@/types/CustomAttribute";
import { ContactSelector, EvidenceSelector } from "@/Features";

export interface AcquisitionFormData {
  acq_id?: number;
  uuid?: string;
  acq_name?: string;
  acq_tool?: string | null;
  acq_type?: string | null;
  format?: string | null;
  tool_version?: string | null;
  size?: number | null;
  size_unit?: "B" | "KB" | "MB" | "GB" | "TB" | "PB" | null;
  storage_id?: string | number | null;
  storage_items?: string[] | null;
  acquire_date?: string | null;
  hash_1?: string | null;
  hash_1_type?: string | null;
  hash_2?: string | null;
  hash_2_type?: string | null;
  duration_hours?: number | null;
  duration_mins?: number | null;
  acquired_by?: string | null;
  acquired_by_id?: number | null;
  person_id?: string | number | null;
  acq_notes?: string | null;
  evidence_id?: string | number | null;
  evidence_number?: string | null;
  case_id?: number | null;
  contact?: string | null;
  custom_attributes?: CustomAttribute[];
  [key: string]:
    | string
    | number
    | string[]
    | boolean
    | null
    | undefined
    | CustomAttribute[]; // Allow dynamic custom field properties
}

export interface EditAcquisitionFormData extends Partial<AcquisitionFormData> {}

interface DefaultAcquisitionFormProps {
  DefaultFormData?: AcquisitionFormData;
  buttonProps?: { cancelText?: string; submitText?: string };
  onSubmit: (
    data: AcquisitionFormData,
    diffData?: EditAcquisitionFormData
  ) => void;
  onCancel: () => void;
  caseId?: number;
  showEvidenceSelector?: boolean;
}

// FORM VALIDATION
const getAcquisitionValidationSchema = (customFields: CustomField[] = []) => {
  const baseSchema = {
    acq_name: z
      .string({
        required_error: "Acquisition name is required",
      })
      .min(1, { message: "Acquisition name is required" }),
    format: z.string().nullable().optional(),
    acq_type: z.string().nullable().optional(),
    acq_tool: z.string().nullable().optional(),
    tool_version: z.string().nullable().optional(),
    size: z.coerce.number().nullable().optional(),
    size_unit: z
      .enum(["B", "KB", "MB", "GB", "TB", "PB"])
      .nullable()
      .optional(),
    storage_id: z.union([z.string(), z.number()]).nullable().optional(),
    storage_items: z.array(z.string()).nullable().optional(),
    acquire_date: z.union([z.string(), z.null(), z.undefined()]).optional(),
    hash_1: z.string().nullable().optional(),
    hash_1_type: z.string().nullable().optional(),
    hash_2: z.string().nullable().optional(),
    hash_2_type: z.string().nullable().optional(),
    duration_hours: z.coerce.number().nullable().optional(),
    duration_mins: z.coerce.number().nullable().optional(),
    acquired_by: z.string().nullable().optional(),
    acquired_by_id: z.number().nullable().optional(),
    person_id: z.union([z.string(), z.number()]).nullable().optional(),
    acq_notes: z.string().nullable().optional(),
    evidence_id: z.coerce.string().nullable().optional(),
  };

  const customFieldValidations = resolveCustomFieldValidations(customFields);

  return z.object({
    ...baseSchema,
    ...customFieldValidations,
  });
};

export const DefaultAcquisitionForm: FC<DefaultAcquisitionFormProps> = ({
  DefaultFormData,
  buttonProps,
  onSubmit,
  onCancel,
  caseId,
  showEvidenceSelector,
}) => {
  // Accounts for edge case when copying Acquisition with single storage_id
  const transformInitialValues = (data?: AcquisitionFormData) => {
    if (!data) return {};

    const transformed = { ...data };

    // If we're in create mode and have storage_id, convert it to storage_items array
    if (!data.acq_id && data.storage_id) {
      transformed.storage_items = [data.storage_id.toString()];
      delete transformed.storage_id;
    }

    return transformed;
  };

  const form = useForm<
    AcquisitionFormData & { linked_contact?: Option; linked_evidence?: Option }
  >({
    mode: "uncontrolled",
    initialValues: {
      size_unit: "GB",
      ...transformInitialValues(DefaultFormData),
      ...transformCustomAttributes(DefaultFormData),
      linked_contact: DefaultFormData?.contact
        ? {
            value: DefaultFormData.person_id,
            label: DefaultFormData.contact,
            data: DefaultFormData.contact,
          }
        : null,
      linked_evidence: DefaultFormData?.evidence_id
        ? {
            value: DefaultFormData.evidence_id,
            label:
              DefaultFormData.evidence_number || "Evidence No. Unavailable",
            data: {
              evidence_id: DefaultFormData.evidence_id,
              evidence_number: DefaultFormData.evidence_number,
            },
          }
        : null,
    } as AcquisitionFormData,
  });

  // Fetch custom fields for acquisitions
  const { data: customFields, isLoading: isLoadingCustomFields } =
    useCustomFields();

  // Model diff data to ensure undefined values are treated as null
  const modelDiffData = (
    diffData: EditAcquisitionFormData
  ): EditAcquisitionFormData => {
    const modeledDiffData: EditAcquisitionFormData = {};

    // Only process fields that exist in the diff data
    Object.keys(diffData).forEach((key) => {
      const value = diffData[key];
      if (value === undefined) {
        modeledDiffData[key] = null;
      } else if (
        key === "custom_attributes" &&
        Array.isArray(value) &&
        value.every(
          (item): item is CustomAttribute =>
            typeof item === "object" && item !== null && "field_id" in item
        )
      ) {
        // Handle custom_attributes array - ensure each attribute's value is not undefined
        modeledDiffData[key] = value.map((attr: CustomAttribute) => ({
          ...attr,
          value: attr.value === undefined ? "" : attr.value,
        }));
      } else if (key.startsWith("custom_attribute_")) {
        // Handle individual custom field properties
        // Convert undefined or empty strings to null for custom fields
        modeledDiffData[key] =
          value === undefined || value === "" ? null : value;
      } else {
        modeledDiffData[key] = value;
      }
    });

    return modeledDiffData;
  };

  const handleSubmit = async () => {
    const formData = form.getValues();
    delete formData.linked_contact; // removed linked_contact from form data as it is not needed for API submission
    delete formData.linked_evidence; // display-only counterpart of evidence_id, not needed for API submission
    delete formData.evidence_number; // read-only field derived from evidence_id; the API does not accept it

    // Transform storage data based on mode
    if (DefaultFormData?.acq_id) {
      // Edit mode: ensure storage_id is properly set
      // Remove storage_items if it exists from form data
      delete formData.storage_items;
    } else {
      // Create mode: ensure storage_items is properly set
      // Remove storage_id if it exists from form data
      delete formData.storage_id;
    }

    // Create validation schema with custom fields
    const validationSchema = getAcquisitionValidationSchema(customFields);
    const validationResult = await validateAsync(validationSchema)(formData);
    if (validationResult?.hasErrors) {
      form.setErrors(validationResult?.errors);
      return;
    }

    const diffData: EditAcquisitionFormData = diffFormData(
      DefaultFormData,
      formData
    );

    // Enhanced diff check for custom fields to catch cleared fields
    if (DefaultFormData?.custom_attributes) {
      const originalCustomFields = transformCustomAttributes(DefaultFormData);

      // Check each original custom field to see if it was cleared
      Object.keys(originalCustomFields).forEach((key) => {
        if (key.startsWith("custom_attribute_")) {
          const originalValue = originalCustomFields[key];
          const currentValue = formData[key];

          // If the field was cleared (undefined, null, or empty string) and had a previous value
          if (
            (currentValue === undefined ||
              currentValue === null ||
              currentValue === "") &&
            originalValue
          ) {
            diffData[key] = currentValue; // Add to diff with the cleared value
          }
        }
      });
    }

    const modeledDiffData = modelDiffData(diffData);

    onSubmit?.(formData, modeledDiffData);
  };

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  const sizeUnits = [
    { value: "B", label: "B" },
    { value: "KB", label: "KB" },
    { value: "MB", label: "MB" },
    { value: "GB", label: "GB" },
    { value: "TB", label: "TB" },
    { value: "PB", label: "PB" },
  ];

  return (
    <div>
      <Grid col={2}>
        {showEvidenceSelector && (
          <EvidenceSelector
            size="sm"
            label="Evidence"
            placeholder="Select evidence item..."
            description="Select an evidence item to acquire."
            valueKey="evidence_id"
            clearable
            openOnFocus={false}
            query={{ case_id: caseId }}
            key={form.key("linked_evidence")}
            {...form.getInputProps("linked_evidence")}
            onChange={(_, option) => {
              form.setFieldValue("linked_evidence", option);
              form.setFieldValue("evidence_id", option?.value);
            }}
          />
        )}
        {showEvidenceSelector && <div></div>}
        <TextInput
          size="sm"
          variant="outlined"
          label="Acquisition Name"
          placeholder="Enter Acquisition name"
          description="The name of the acquisition"
          required
          autoFocus
          key={form.key("acq_name")}
          {...form.getInputProps("acq_name")}
        />
        <Grid col={2}>
          <FormatSelector
            key={form.key("format")}
            {...form.getInputProps("format")}
          />
          <AcquisitionTypeSelector
            key={form.key("acq_type")}
            {...form.getInputProps("acq_type")}
          />
        </Grid>
        <SoftwareSelector
          key={form.key("acq_tool")}
          {...form.getInputProps("acq_tool")}
        />
        <TextInput
          size="sm"
          variant="outlined"
          label="Software Version"
          placeholder="Enter software version"
          description="Version of software used"
          key={form.key("tool_version")}
          {...form.getInputProps("tool_version")}
        />
        <Grid col={2}>
          <TextInput
            size="sm"
            variant="outlined"
            label="Size"
            placeholder="0"
            min={0}
            description="Size of the acquisition"
            type="number"
            key={form.key("size")}
            {...form.getInputProps("size")}
          />
          <SelectBox
            size="sm"
            variant="outlined"
            label="Unit"
            placeholder="Select unit"
            description="Size unit"
            data={sizeUnits}
            key={form.key("size_unit")}
            {...form.getInputProps("size_unit")}
          />
        </Grid>
        <StorageSelector
          caseId={caseId}
          mode={DefaultFormData?.acq_id ? "single" : "multiple"}
          key={form.key(
            DefaultFormData?.acq_id ? "storage_id" : "storage_items"
          )}
          {...form.getInputProps(
            DefaultFormData?.acq_id ? "storage_id" : "storage_items"
          )}
        />
      </Grid>
      <FormSection
        title={"Advanced Details"}
        defaultOpen={!!DefaultFormData}
        style={{ marginTop: 18 }}
      >
        <Grid col={2}>
          <TextInput
            size="sm"
            variant="outlined"
            label="Hash 1"
            placeholder="Enter hash"
            description="Hash value"
            key={form.key("hash_1")}
            {...form.getInputProps("hash_1")}
          />
          <HashTypeSelector
            key={form.key("hash_1_type")}
            {...form.getInputProps("hash_1_type")}
          />
          <TextInput
            size="sm"
            variant="outlined"
            label="Hash 2"
            placeholder="Enter hash"
            description="Second hash value"
            key={form.key("hash_2")}
            {...form.getInputProps("hash_2")}
          />
          <HashTypeSelector
            key={form.key("hash_2_type")}
            {...form.getInputProps("hash_2_type")}
          />
          <DateInput
            size="sm"
            variant="outlined"
            label="Acquire Date"
            description="Date when acquired"
            clearable={true}
            arrow={true}
            enableCalendar={true}
            format={
              getDateFormat({
                isMoment: true,
                includeTime: true,
              }) as DateInputFormat
            }
            key={form.key("acquire_date")}
            {...form.getInputProps("acquire_date")}
          />
          <Grid col={2}>
            <TextInput
              size="sm"
              variant="outlined"
              label="Duration Hours"
              placeholder="Hours"
              description="Acquisition duration hours"
              type="number"
              min={0}
              key={form.key("duration_hours")}
              {...form.getInputProps("duration_hours")}
            />
            <TextInput
              size="sm"
              variant="outlined"
              label="Duration Minutes"
              placeholder="Minutes"
              description="Acquisition duration minutes"
              type="number"
              min={0}
              key={form.key("duration_mins")}
              {...form.getInputProps("duration_mins")}
            />
          </Grid>
          <UserSelector
            label="Acquired By"
            query={{
              case_id: caseId,
              includeInactive: false,
              includeObservers: false,
            }}
            key={form.key("acquired_by_id")}
            {...form.getInputProps("acquired_by_id")}
          />
          <ContactSelector
            query={{ case_id: caseId }}
            size="sm"
            variant="outlined"
            clearable={true}
            allowCreateNew={true}
            onContactCreated={(contact) => {
              form.setFieldValue("person_id", contact.client_id);
              form.setFieldValue("linked_contact", {
                value: contact.client_id,
                label: contact.name,
                data: contact,
              });
            }}
            key={form.key("linked_contact")}
            {...form.getInputProps("linked_contact")}
            onChange={(_, option) => {
              form.setFieldValue("linked_contact", option);
              form.setFieldValue("person_id", option?.value);
            }}
          />
        </Grid>
        <div style={{ marginTop: "1rem" }}>
          <TextAreaInput
            size="sm"
            variant="outlined"
            label="Notes"
            placeholder="Enter notes on this acquisition"
            description="Any additional notes on this acquisition"
            minRows={5}
            key={form.key("acq_notes")}
            {...form.getInputProps("acq_notes")}
          />
        </div>
      </FormSection>
      <FormSection
        title={"Custom Fields"}
        defaultOpen={!!DefaultFormData}
        style={{ marginTop: 15 }}
      >
        <Grid col={2}>
          {customFields?.map((field: CustomField) => (
            <CustomFieldRender
              key={form.key(`custom_attribute_${field.field_id}`)}
              field={field}
              inputProps={form.getInputProps(
                `custom_attribute_${field.field_id}`
              )}
              onChange={() => {}} // Add empty onChange handler to satisfy type requirements
            />
          ))}
        </Grid>
      </FormSection>
      <ButtonMenu>
        <Button size="xs" variant="subtle" onClick={handleCancel}>
          {buttonProps?.cancelText || "Cancel"}
        </Button>
        <Button
          size="xs"
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoadingCustomFields}
        >
          {buttonProps?.submitText || "Submit"}
        </Button>
      </ButtonMenu>
    </div>
  );
};
